/**
 * O conteúdo de fábrica de "Cenas, Ações e Fluxo de Jogo".
 *
 * <p><b>Por que este arquivo não se chama {@code cenasAcoesFluxo.ts}:</b> ele se chamava,
 * e o nome colidia com {@code CenasAcoesFluxo.tsx} em disco case-insensitive (Windows) —
 * o TypeScript resolvia o import do componente para este módulo e o build quebrava só
 * lá. Mesma lição do {@code geometriaDaGrade.ts} do calendário: módulo de apoio não
 * pode ter o nome do componente mudando só a caixa.
 *
 * <p><b>De onde cada linha veio.</b> Três fontes, e elas não concordam:
 *
 * <ul>
 *   <li><b>épico-01</b> — `docs/cards-beta/epico-01-cenas-acoes-fluxo.md` no repositório
 *       do jogo. É a fotografia do que está construído: o que o motor faz hoje, o que
 *       falta, e dez decisões que o time ainda não tomou.</li>
 *   <li><b>GDD v3.3</b> — `GDD_MistikIsland_v3.3.html`. É o cânone de design: o que o
 *       jogo <i>deve</i> ser, com cada sistema marcado como Canônico, Em revisão ou Em
 *       aberto.</li>
 *   <li><b>o código</b> — `SceneType`, `SceneActionKind`, `SceneEffectExecutor` e
 *       `RequirementChecker` no backend do jogo. Onde os dois documentos divergem sobre
 *       um catálogo, o código é o desempate factual: ele é o que roda.</li>
 * </ul>
 *
 * <p><b>Por que o conteúdo mora em código e não numa migration de seed.</b> Isto é o
 * ponto de partida da conversa, não dado do produto: quando o Theo decidir os conflitos,
 * a linha que vale é a que ficou gravada pelo time, e este arquivo vira só o que a
 * página mostra a quem abre a tela pela primeira vez. Um seed em SQL faria a mesma
 * informação existir em dois lugares — e o CLAUDE.md já conta como isso termina.
 *
 * <p><b>A tabela de conflitos é a razão da página existir.</b> As demais resumem; ela
 * pergunta. A última coluna nasce vazia de propósito — um chute pré-preenchido se lê
 * como decisão tomada, e o time passaria a discutir o que a tela inventou.
 */
/*
 * Os construtores `tabela`, `col` e `familiasNaOrdem` eram privados daqui e subiram
 * para `tabelasEditaveis` quando o conteúdo de Personagem nasceu — as duas páginas
 * montam tabela de fábrica do mesmo jeito, e o id de linha (`l1`, `l2`…) precisa sair
 * igual nos dois, porque é ele que casa a linha com a família e com as entregas.
 */
import { iconeDeAcao, iconeDeTipoDeCena, iconeDoMenu } from './iconesDeConteudo';
import type { GrupoDeQuadros, SecaoDeQuadros } from './PaginaDeQuadros';
import { col, familiasNaOrdem, montarTabela as tabela } from './tabelasEditaveis';
import type { Tabela } from './tabelasEditaveis';

/**
 * As cinco famílias que pintam o fundo das linhas em "As Cenas" e "As Ações".
 *
 * <p><b>A cor é a mesma nas duas tabelas</b>, e é o ponto: o verde diz "lugar" numa e na
 * outra, então dá para ver que <i>Viajar</i> pertence a <i>AREA</i> sem ler nada. As duas
 * ficam longe uma da outra na página, e sem a cor esse parentesco só aparece para quem já
 * o conhecia.
 *
 * <p><b>Cinco, e não nove.</b> A primeira versão separava coleta de criação, combate de
 * evento, e conhecimento de produção — distinções verdadeiras, que a tabela já faz com
 * palavras. Nove tons sobre fundo escuro deixaram de agrupar e passaram a ser um
 * mostruário: com essa quantidade o olho para de ler famílias e passa a ler cores, uma a
 * uma. Menos cor, mais grupo.
 *
 * <p><b>Só decoram.</b> O que cada linha é continua escrito nela — a cor agrupa, não
 * informa, e por isso ninguém fica sem entender a tabela se não distinguir os tons. É
 * também por isso que ficar <i>sem</i> cor é uma resposta legítima.
 *
 * <p>Os tons saem da paleta que o menu já usa nas seções, para a página não inventar um
 * segundo vocabulário de cor dentro do mesmo produto.
 */
export const FAMILIAS = {
  /** Verde — onde se está: região, área, praça, local, e o que move entre eles. */
  lugar: 'lugar',
  /** Azul — quem se encontra, e o comércio que eles fazem. */
  npc: 'npc',
  /** Vermelho — o que acontece: o começo, os encontros, os eventos de história. */
  evento: 'evento',
  /** Amarelo — o que se tira do mundo e o que se faz com isso. */
  producao: 'producao',
  /** Roxo — parar: descansar, dormir, sonhar. */
  descanso: 'descanso',
} as const;

/** O nome de cada família na coluna que deixa trocá-la. */
export const CORES_DAS_LINHAS: { id: string; rotulo: string }[] = [
  { id: FAMILIAS.lugar, rotulo: 'Lugar' },
  { id: FAMILIAS.npc, rotulo: 'NPC' },
  { id: FAMILIAS.evento, rotulo: 'Evento' },
  { id: FAMILIAS.producao, rotulo: 'Coleta e criação' },
  { id: FAMILIAS.descanso, rotulo: 'Descanso' },
];


/**
 * As entregas em que cada linha pode entrar — os checkboxes de escopo.
 *
 * <p>São as quatro portas de saída do Mistik Island, na ordem do funil: o alpha do
 * time, o beta fechado, o que lança com a Season 1 e o que fica para depois. A régua é
 * a mesma em toda tabela da página de propósito — escopo só informa quando dá para
 * comparar entre quadros.
 *
 * <p><b>Tudo nasce desmarcado.</b> Mesma razão da coluna "Decisão do Theo" dos
 * conflitos: um checkbox pré-marcado se lê como decisão tomada, e o time passaria a
 * discutir o escopo que a tela inventou.
 */
export const ENTREGAS: { id: string; rotulo: string }[] = [
  { id: 'alpha', rotulo: 'Alpha interno' },
  { id: 'beta', rotulo: 'Closed Beta' },
  { id: 's1', rotulo: 'Season 1' },
  { id: 's2', rotulo: 'Season 2+' },
];

// ---------------------------------------------------------------- os conflitos

/**
 * Onde o épico-01 e o GDD v3.3 dizem coisas diferentes sobre a mesma regra.
 *
 * <p>Não entram aqui as diferenças de <i>vocabulário</i> nem o que um diz e o outro
 * apenas cala — só os pontos em que seguir um deles implica <b>não</b> seguir o outro,
 * e em que a implementação precisaria escolher no escuro.
 */
const CONFLITOS = tabela(
  [
    col('assunto', 'Assunto'),
    col('epico', 'O que o épico-01 diz', true),
    col('gdd', 'O que o GDD v3.3 diz', true),
    col('peso', 'Peso'),
    col('decisao', 'Decisão do Theo', true),
  ],
  [
    [
      'Energia como custo',
      'Não existe. O único custo de ação é `costTurns`, e nenhum dos 20 efeitos do catálogo mexe em energia.',
      'Canônico e estrutural: "toda ação tem custo de energia, independente do custo em turno". Combater não gasta turno e gasta energia por ação; energia negativa aplica a condição esgotado. O próprio GDD chama isto de "a mudança mais estrutural desta versão".',
      'Alto',
      '',
    ],
    [
      'Sono',
      'Vivo: a decisão §2.10 discute se o Sono entra como modificador da fórmula ou pelo atributo efetivo, e o risco de 1.C fala em "espiral de fadiga" a partir do turno 9.',
      'Removido. As inconsistências 6, 7 e 8 foram fechadas na v3.1 mandando o Sono para o backlog: "sem o sono, todo o jogo passa a contar apenas turno do relógio".',
      'Alto',
      '',
    ],
    [
      'Tipos de cena',
      'Fala em "11 tipos". O código tem 12: START, REGIAO, AREA, PRACA, LOCAL, NPC, MOB, REC, EVENTO, MERC, JORNAL, RANK.',
      'Sete famílias, com outros nomes: Área, Local, NPCs, Combate, Eventos (Encontros, Exploração, Encontro Selvagem), Coleta e Criação. Não menciona PRACA, MERC, JORNAL, RANK, START nem REGIAO; tem Criação, que não existe no código.',
      'Alto',
      '',
    ],
    [
      'O que explorar faz',
      'Cobra `turn.explore_cost` e revela `narrativeExplore` só na primeira vez. É narrativa extra.',
      'É o investimento que destrava a área: depois de explorada, ela libera caçar, coletar madeira, forragear e o acesso a locais de mineração e de inimigos. Antes disso, a chegada dá uma "cena de estrada" com opções limitadas.',
      'Alto',
      '',
    ],
    [
      'O que viajar cobra',
      '1 turno ao trocar de área, 0 na mesma área, `costTurns` da conexão sobrescreve. Ainda não implementado (§2.8).',
      'Um turno e energia, mais um teste de vitalidade nos bastidores: falhar acrescenta um nível de cansaço, e a carga piora a chance de falhar. A tentativa rende experiência, e a tabela de encontros do destino é consultada na chegada.',
      'Alto',
      '',
    ],
    [
      'Quantos turnos tem o dia',
      'O motor roda com 8: a migration V18 semeia `turn.per_day = 8`, o default de `turnsRemaining` é 8 e o `useTimePhase` faz fallback 8. (épico-02, §1.2)',
      'Doze, do nascer do sol à madrugada. Toda a matemática de períodos e a fórmula de dormir só fecham com 12.',
      'Alto',
      '',
    ],
    [
      'Quando o mundo vira o dia',
      '`world.day_advance_hour_utc = 6`, que é 03:00 em Brasília. (épico-02, §1.2)',
      '"Vira o dia às 6:00 no horário do Brasil" — três horas depois do que o motor faz.',
      'Médio',
      '',
    ],
    [
      'Faixas do dia',
      'Binário. O token `time:` só conhece `day` e `night`, decididos por `time.night_starts_at` — e compara o turno absoluto, que nunca reseta, então tende a ficar sempre verdadeiro a partir do 2º dia.',
      'Oito faixas com vocação própria (nascer, manhã, meio-dia, tarde, pôr do sol, noite, meia-noite, madrugada). A inconsistência 12 registra que os turnos 1 e 7 não têm lado no corte dia/noite.',
      'Médio',
      '',
    ],
    [
      'Quais ações gastam turno',
      'Nomeia só viajar e explorar como gastadoras; `costTurns` da ação é override de conteúdo.',
      'Lista maior: viajar, explorar, coletar, caçar, minerar, descansar e criar. Não gastam: conversar, deslocar na área, negociar, usar item, interagir e combater.',
      'Médio',
      '',
    ],
    [
      'Críticos do dado',
      'Crava os números: 1 → −5 e 12 → +5 no resultado (§2.1, story I1.1b).',
      'Diz que o mínimo derruba e o máximo eleva, sem cravar o valor — e registra na inconsistência 5 que há dois críticos convivendo (o do teste e o do ataque), que precisam ser separados ou unificados.',
      'Médio',
      '',
    ],
    [
      'Encontro selvagem',
      'Fora do escopo: 1.B manda para os Épicos 6 e 8 e diz que o efeito `roll_encounter` "hoje não existe".',
      'Canônico e transversal: toda ação que consome tempo consulta a tabela de encontros da área, ponderada pela faixa do dia. É "o mecanismo que impede o jogo de virar planilha".',
      'Médio',
      '',
    ],
    [
      'Reputação',
      'Motor pronto: `delta_rep` por NPC e facção, gate `rep:*`, e seis faixas (Hostil ≤ −51 … Devoto ≥ 75) que hoje só existem no step do Cypress (§2.7).',
      'Silêncio. Reputação não aparece como sistema em nenhuma seção, nem no status dos sistemas — nem como canônica, nem como em aberto.',
      'Médio',
      '',
    ],
    [
      'Conjunto de atributos',
      'Pergunta em aberto (§2.2): 6 ou 7? Recomenda 6 e registra que outros documentos citam Carisma.',
      'Responde: seis, com custo crescente. Isto fecha a §2.2 — não é conflito, é a decisão que faltava.',
      'Resolvido',
      '',
    ],
  ]
);

// ------------------------------------------------------- as decisões do épico

/** As dez decisões da §2 do épico-01, com a recomendação que ele mesmo dá. */
const DECISOES = tabela(
  [
    col('pergunta', 'Pergunta em aberto', true),
    col('recomendacao', 'O que o épico recomenda', true),
    col('quem', 'Quem decide'),
    col('decisao', 'Decisão do Theo', true),
  ],
  [
    [
      '§2.1 — A fórmula do teste: embutir a do design no motor ou manter genérica?',
      'Um preset "padrão Mistik" (1d12 + primário ×2 + críticos ∓5), mantendo fórmula livre para casos especiais. Decisão-fundação: o resolver é compartilhado com o Combate.',
      'Game designer + dev',
      '',
    ],
    [
      '§2.2 — Seis atributos ou sete?',
      'Os seis do design: Força, Destreza, Agilidade, Vitalidade, Intelecto, Percepção. O GDD v3.3 já confirma seis.',
      'Game designer',
      '',
    ],
    [
      '§2.3 — `dec_counter` deve travar em 0?',
      'Clampar em 0, alinhando com `delta_gold` e HP que já saturam. Hoje decrementar de 0 dá −1.',
      'Game designer + dev',
      '',
    ],
    [
      '§2.4 — Compra sem saldo: zerar o ouro em silêncio ou abortar?',
      'Um efeito `pay_gold` que debita e aborta se faltar, com o botão visível-travado. Hoje zera o ouro e entrega o item — "item de graça". Fechar antes de carregar qualquer ação de compra.',
      'Game designer + dev',
      '',
    ],
    [
      '§2.5 — Token desconhecido deve falhar seguro?',
      'Travar em vez de liberar, nas três fontes de fail-open, e validar prefixo e aridade já no salvamento do admin. Hoje um typo abre conteúdo de segredo para todos.',
      'Dev (risco de produto)',
      '',
    ],
    [
      '§2.6 — Reabilitar `GET /scenes/{id}`?',
      'Manter desativado no Beta; o contrato atual já protege contra vazamento de existência. Reabilitar custa ~0 (descomentar).',
      'Dev',
      '',
    ],
    [
      '§2.7 — Onde moram os limiares das faixas de reputação?',
      'Centralizar no backend (candidatas a `game_config`) antes do HUD. Ao centralizar, revisar a assimetria: 2 faixas negativas contra 4 positivas.',
      'Game designer + dev',
      '',
    ],
    [
      '§2.8 — Como o `costTurns` da conexão interage com o default de viagem?',
      'Default 1 para troca de área e 0 na mesma área, com `costTurns` da conexão como override. A regra de 1 turno já é design; o que está aberto é só o override.',
      'Game designer + dev',
      '',
    ],
    [
      '§2.9 — Mensagem de bloqueio humanizada: Beta ou depois?',
      'Auto-derivada do token no Beta ("requer Intelecto 7"), campo autoral por ação em S1. Sempre o requisito concreto, nunca "requisito não atendido".',
      'Game designer',
      '',
    ],
    [
      '§2.10 — De onde entra a penalidade de Sono na fórmula?',
      'Modificador temporário nomeado, para o overlay poder exibir "−2 sono". Ver o conflito acima: o GDD v3.3 removeu o Sono, então esta decisão pode ter perdido o objeto.',
      'Game designer + dev',
      '',
    ],
  ]
);

// ------------------------------------------------------------- os catálogos

const TIPOS_DE_CENA = tabela(
  [
    col('tipo', 'Tipo (código)'),
    col('oQue', 'O que é', true),
    col('conexoes', 'Conexões automáticas', true),
    col('gdd', 'No GDD v3.3'),
  ],
  [
    ['START', 'Introdução/tutorial.', 'Nenhuma.', 'não menciona'],
    ['REGIAO', 'Divisão territorial, o nível mais alto. São sete.', 'Filhas + viagens.', 'sim, como conceito'],
    ['AREA', 'A unidade de deslocamento. Sete por região, 49 no total.', 'Filhos (PRACA/LOCAL/NPC) + viagens stored.', 'Área'],
    ['PRACA', 'Praça/centro de uma área.', 'Filhos + "Voltar".', 'não menciona'],
    ['LOCAL', 'A unidade de interação: taverna, forja, mina. Pode conter outros locais.', 'Filhos + "Voltar".', 'Local'],
    ['NPC', 'Personagem não-jogável, com diálogo.', 'Só "Voltar".', 'NPCs'],
    ['MOB', 'Monstro/encontro de combate.', 'Só "Voltar".', 'Combate'],
    ['REC', 'Recurso coletável.', 'Só "Voltar".', 'Coleta'],
    ['EVENTO', 'Evento de história.', 'Só "Voltar".', 'Eventos'],
    ['MERC', 'Interface de mercador/loja.', 'Só "Voltar".', 'não menciona'],
    ['JORNAL', 'Jornal/tracker de quests.', 'Só "Voltar".', 'não menciona'],
    ['RANK', 'Ranking/placar.', 'Só "Voltar".', 'não menciona'],
    ['—', 'Criação (alquimia, forja, couraria) como tipo de cena própria.', '—', 'Criação — não existe no código'],
  ]
);

const CAMPOS = tabela(
  [col('campo', 'Campo'), col('onde', 'Onde'), col('oQue', 'O que faz', true)],
  [
    ['block_back', 'cena', 'Esconde o "Voltar".'],
    ['manualConnections', 'cena', 'Ignora a auto-geração; só usa as conexões cadastradas, na ordem de `sort_order`.'],
    ['hiddenWhenBlocked', 'conexão / ação', 'Some quando travada, em vez de aparecer visível-travada.'],
    ['onNavigateSetFlags', 'conexão', 'Seta flags ao percorrer.'],
    ['onEnterSetFlags', 'cena / variante', 'Seta flags ao chegar.'],
    ['onEnterSetFlagsOverride', 'variante', 'Substitui o `onEnterSetFlags` base quando a variante vence.'],
    ['costTurns', 'conexão / ação', 'Custo de turno, override do default.'],
    ['checkDifficulty', 'ação SKILL_CHECK', 'A CD do teste. Nulo ou 0 = sucesso garantido; 999 = falha garantida.'],
    ['onEnterCheck', 'cena / variante', 'Teste oculto ao entrar. Ainda não existe — é a story I1.1a.'],
    ['priority', 'variante', 'Entre as variantes cuja condição é atendida, a de maior prioridade vence.'],
  ]
);

const TOKENS = tabela(
  [
    col('token', 'Token'),
    col('testa', 'O que testa', true),
    col('exemplo', 'Exemplo'),
    col('nota', 'Observação', true),
  ],
  [
    ['flag / !flag', 'Flag ativa ou ausente.', 'flag:tocha_acesa', ''],
    ['seen:ID', 'Já visitou a cena.', 'seen:9301', ''],
    ['item:ID:QTY', 'Posse de item.', 'item:pergaminho:1', 'Ramo legado: hoje sempre libera (fail-open).'],
    ['attr:NAME:OP:N', 'Atributo do personagem.', 'attr:intellect:GTE:7', 'Sem operador, libera por aridade curta.'],
    ['counter:NAME:OP:N', 'Contador acumulado.', 'counter:treinos:GTE:3', ''],
    ['rep:npc:ID:OP:N', 'Reputação com um NPC.', 'rep:npc:garrick:GTE:5', ''],
    ['rep:faction:ID:OP:N', 'Reputação com uma facção.', 'rep:faction:khotla:GTE:50', ''],
    ['turn', 'Turno atual do personagem.', 'turn:GTE:8', ''],
    ['time', 'Dia ou noite.', 'time:night', 'Só conhece day/night — ver o conflito das oito faixas.'],
    ['day', 'Dia do personagem.', 'day:RANGE:3:5', ''],
    ['world_day', 'Dia do mundo.', 'world_day:GTE:10', ''],
    ['day_backlog', 'Quantos dias o personagem está atrás do mundo.', 'day_backlog:GTE:2', ''],
    ['quest:ID:STATUS', 'Status de uma quest.', 'quest:5:complete', 'Stub: sempre passa, até o Épico 9 (story I1.2).'],
    ['world_flag', 'Flag global do mundo.', 'world_flag:incendio', 'World-quest. O fail-safe de I1.5 não pode quebrá-lo.'],
    ['world_counter', 'Contador global.', 'world_counter:ratos:GTE:500', 'World-quest.'],
    ['world_top', 'O personagem está no topo do ranking da world-quest.', 'world_top:10', 'World-quest.'],
    ['contributed', 'O personagem contribuiu para a world-quest.', 'contributed:caçada', 'World-quest.'],
    ['world_quest_state', 'Estado da world-quest.', 'world_quest_state:aberta', 'World-quest.'],
    [
      'qualquer outro',
      'Nada — cai no `default -> true` e libera a ação.',
      'flgg:x',
      'É o fail-open da §2.5: um typo de admin abre conteúdo de segredo.',
    ],
  ]
);

const COMPARADORES = tabela(
  [col('simbolo', 'Comparador'), col('oQue', 'O que faz', true)],
  [
    ['GTE / MIN', 'Maior ou igual. É também o legado `:N`.'],
    ['LTE / MAX', 'Menor ou igual.'],
    ['GT / LT', 'Estritamente maior / menor.'],
    ['EQ / NEQ', 'Igual / diferente.'],
    ['RANGE:A:B', 'Entre A e B, inclusive.'],
    [', (vírgula)', 'E — entre grupos.'],
    ['| (barra)', 'OU — dentro do grupo.'],
    ['! (exclamação)', 'Negação.'],
  ]
);

const EFEITOS = tabela(
  [col('efeito', 'Efeito'), col('oQue', 'O que faz', true), col('nota', 'Armadilha conhecida', true)],
  [
    ['set_flag / clear_flag', 'Liga e desliga uma flag do personagem.', ''],
    ['inc_counter / dec_counter', 'Soma ou subtrai 1 de um contador.', '`dec_counter` não tem piso: de 0 vai a −1. É a §2.3.'],
    ['set_counter', 'Fixa o valor do contador, criando-o se não existir.', ''],
    ['delta_attr', 'Soma ou subtrai de um atributo.', ''],
    ['delta_gold', 'Soma ou subtrai ouro.', 'Satura em 0: comprar sem saldo zera o ouro, aplica os demais efeitos e entrega o item. É a §2.4.'],
    ['give_item / consume_item', 'Dá ou consome item do inventário.', ''],
    ['delta_rep', 'Muda a reputação com NPC e/ou facção, em um ou mais alvos.', 'Sem auto-propagação entre NPC e facção — cada alvo é explícito.'],
    ['goto_scene', 'Leva para outra cena.', 'Vence o `targetScene` da ação.'],
    ['start_minigame', 'Abre um minigame.', 'Suprime a navegação por `defaultTarget`.'],
    ['advance_day', 'Avança o dia.', ''],
    ['grant_companion / remove_companion', 'Dá ou tira um companheiro.', ''],
    ['world_inc_counter', 'Soma no contador global do mundo.', 'World-quest (Vol II).'],
    ['world_set_flag / world_clear_flag', 'Liga e desliga uma flag global.', 'World-quest. Muda a cena para todos ao mesmo tempo.'],
    ['world_check_threshold', 'Verifica se o limiar coletivo foi atingido.', 'World-quest.'],
    ['world_register_kill', 'Registra um abate na contagem coletiva.', 'World-quest.'],
    ['— (não existe)', 'Custo/ganho de energia.', 'O GDD v3.3 o exige em toda ação. Ver o conflito "Energia como custo".'],
    ['— (não existe)', 'Sortear encontro selvagem (`roll_encounter`).', 'O próprio épico o sugere e diz que hoje não existe.'],
  ]
);

const ORDEM_DA_ACAO = tabela(
  [col('etapa', 'Etapa'), col('oQue', 'O que acontece', true)],
  [
    ['1. Condição', 'O `condition` decide se a ação aparece, aparece travada, ou some (`hiddenWhenBlocked`). É reavaliado a cada render — um efeito pode revelar outra ação na mesma cena.'],
    ['2. Custo', 'O `costTurns` é cobrado. No SKILL_CHECK, é cobrado mesmo na falha: tentar já consome tempo.'],
    ['3. Efeitos', 'GENERIC aplica `effects`. SKILL_CHECK bifurca em `successEffects` / `failEffects` — e não usa o `effects` base.'],
    ['4. Navegação', '`goto_scene` (efeito) vence `targetScene` (ação); `start_minigame` suprime a navegação por `defaultTarget`.'],
    ['5. Resposta', 'Narrativa inline (substitui o texto da cena) ou em pergaminho (overlay), mais `feedbackMessages` (toasts).'],
  ]
);

const TIPOS_DE_ACAO = tabela(
  [col('kind', 'Kind'), col('oQue', 'O que é', true), col('estado', 'Estado')],
  [
    ['GENERIC', 'Botão que executa custo → efeitos → navegação e responde.', 'Pronto e testado'],
    ['SKILL_CHECK', 'Rola contra a `checkDifficulty` e ramifica sucesso/falha. Indicador 🎲 no botão, overlay de dado.', 'Pronto e testado'],
    ['MINIGAME', 'Existe no enum do código; o épico-01 não o descreve em lugar nenhum.', 'Não documentado'],
  ]
);

const TESTE_BASE = tabela(
  [col('parcela', 'Parcela'), col('oQue', 'O que entra', true), col('fonte', 'Fonte')],
  [
    ['Dado de Sorte', '1d12.', 'Ambas'],
    ['Atributo primário', 'Vale ×2. Qual é o primário depende da ação (viajar → Vitalidade; coletar → Percepção; alquimia → Intelecto).', 'Ambas; a tabela por ação é do GDD'],
    ['Atributos secundários', 'Somam ×1.', 'Ambas'],
    ['Perícia', 'O nível da perícia correspondente.', 'Ambas'],
    ['Equipamento / ferramenta', 'O bônus do que está equipado.', 'Ambas'],
    ['Bônus de situação', 'Modificadores temporários.', 'Ambas'],
    ['− Penalidade', 'Carga, condições. O épico incluía o Sono aqui — o GDD v3.3 o removeu.', 'Conflito'],
    ['Crítico', 'épico: 1 → −5, 12 → +5. GDD: o mínimo derruba e o máximo eleva, sem número; e há dois críticos convivendo.', 'Conflito'],
    ['Comparação', 'Contra a CD declarada da ação, a dificuldade da receita, ou o teste do oponente.', 'Ambas'],
  ]
);

const FAIXAS_DO_DIA = tabela(
  [col('turno', 'Turno'), col('faixa', 'Faixa'), col('vocacao', 'Vocação', true)],
  [
    ['1', 'Nascer do sol', 'Abertura de oficinas, provisões, resultado dos leilões da noite.'],
    ['2 e 3', 'Manhã', 'Viagem, exploração, caça diurna.'],
    ['4', 'Meio-dia', 'Criação, descanso, conversa com NPCs.'],
    ['5 e 6', 'Tarde', 'Coleta, mineração, progresso em quests.'],
    ['7', 'Pôr do sol', 'Última janela de oficina e preparação para a noite.'],
    ['8 e 9', 'Noite', 'Primeira janela de dormir. Coletas exclusivas noturnas.'],
    ['10', 'Meia-noite', 'Criaturas noturnas. Risco alto.'],
    ['11 e 12', 'Madrugada', 'Recursos raros e criaturas exclusivas. Risco máximo, e a manhã seguinte comprometida.'],
  ]
);

/**
 * O que cada ação cobra, e se o resultado dela é jogado ou dado.
 *
 * <p><b>A coluna da fórmula sai da tabela "Quem é primário em cada ação" do GDD v3.3</b>,
 * aplicada à fórmula-base: primário conta <b>×2</b>, secundário ×1. Ela não é um segundo
 * lugar onde a fórmula mora — é a mesma de "O teste base", instanciada por ação, que é a
 * única forma de responder "qual é a conta de minerar?" sem obrigar quem lê a montá-la de
 * cabeça.
 *
 * <p>Coletar, caçar e minerar viraram três linhas, e criar virou três: o primário de cada
 * uma é diferente, e a linha única que existia aqui escondia justamente o que a coluna
 * nova veio mostrar.
 */
const ACOES = tabela(
  [
    col('acao', 'Ação'),
    col('turno', 'Gasta turno?'),
    col('energia', 'Gasta energia? (GDD)'),
    col('teste', 'Tem teste?'),
    col('formula', 'Fórmula do teste', true),
    col('nota', 'Observação', true),
  ],
  [
    [
      'Viajar (troca de área)',
      'Sim, 1',
      'Sim',
      'Sim, oculto',
      '1d12 + VIT×2 + perícia + equipamento − penalidade de carga',
      'O turno é regra de design, ainda não implementada. O teste roda nos bastidores: falhar acrescenta um nível de cansaço, e a tabela de encontros do destino é consultada na chegada.',
    ],
    [
      'Deslocar (mesma área)',
      'Não',
      'Não declarado',
      'Não',
      '—',
      '`costTurns` da conexão sobrescreve e cobra mesmo aqui.',
    ],
    [
      'Explorar',
      'Sim, `turn.explore_cost`',
      'Sim',
      'Não',
      '—',
      'Uma vez por área. No GDD, é o que destrava caçar, coletar e minerar ali.',
    ],
    [
      'Caçar',
      'Sim (GDD)',
      'Sim',
      'Sim',
      '1d12 + PER×2 + perícia + equipamento',
      'O rendimento sai da faixa do resultado, e a faixa do dia muda a tabela de caça.',
    ],
    [
      'Coletar',
      'Sim (GDD)',
      'Sim',
      'Sim',
      '1d12 + PER×2 + VIT + perícia + ferramenta',
      'O épico-01 não nomeia coletar como gastador de turno — ver o conflito "Quais ações gastam turno".',
    ],
    ['Minerar', 'Sim (GDD)', 'Sim', 'Sim', '1d12 + VIT×2 + perícia + ferramenta', 'Idem.'],
    [
      'Alquimia',
      'Sim (GDD)',
      'Sim',
      'Sim',
      '1d12 + INT×2 + PER + perícia + ferramenta',
      'Contra a dificuldade da receita. A qualidade do item sai da faixa do resultado.',
    ],
    [
      'Forjar',
      'Sim (GDD)',
      'Sim',
      'Sim',
      '1d12 + DES×2 + PER + perícia + ferramenta',
      'Idem.',
    ],
    ['Couraria', 'Sim (GDD)', 'Sim', 'Sim', '1d12 + DES×2 + perícia + ferramenta', 'Idem.'],
    [
      'Escrever',
      'Sim (GDD)',
      'Sim',
      'Sim',
      '1d12 + INT×2 + DES + perícia',
      'Copiar para pergaminho ou grimório.',
    ],
    ['Estudar', 'Sim (GDD)', 'Sim', 'Sim', '1d12 + INT×2 + perícia', 'Memorizar o que foi copiado.'],
    [
      'Descansar',
      'Sim (GDD)',
      'Recupera',
      'Não',
      '—',
      'Consulta a tabela de encontros da área, como toda permanência no mundo.',
    ],
    [
      'Combater',
      'Não',
      'Sim, por ação',
      'Sim, oposto',
      'O total do atacante contra o total do defensor, e não contra uma CD fixa',
      'É o custo em energia que impede lutar o dia inteiro — o turno não passa.',
    ],
    [
      'Conversar / negociar / usar item / interagir',
      'Não',
      'Não declarado',
      'Não',
      '—',
      'As duas fontes concordam. Uma dessas interações só vira teste se for cadastrada como ação `SKILL_CHECK`.',
    ],
    [
      'Ação de cena `GENERIC`',
      '`costTurns` da ação',
      'Não existe no motor',
      'Não',
      '—',
      'Resultado determinístico: custo → efeitos → navegação.',
    ],
    [
      'Ação de cena `SKILL_CHECK`',
      '`costTurns`, cobrado mesmo na falha',
      'Não existe no motor',
      'Sim, visível',
      'Fórmula configurável por ação, comparada com `checkDifficulty`',
      'Hoje o resolver aceita um `XdY` genérico: o 1d12, o primário ×2 e os críticos são a story I1.1b.',
    ],
  ]
);

const REPUTACAO = tabela(
  [col('faixa', 'Faixa'), col('valor', 'Valor')],
  [
    ['Hostil', '≤ −51'],
    ['Desconfiado', '−50 a −1'],
    ['Neutro', '0 a 24'],
    ['Amigável', '25 a 49'],
    ['Aliado', '50 a 74'],
    ['Devoto', '≥ 75'],
  ]
);

/**
 * A prosa que apresenta cada quadro.
 *
 * <p><b>Ela é conteúdo, não legenda.</b> Estava escrita em JSX, dentro da página, e por
 * isso mudá-la era um commit — numa tela cuja razão de existir é o time escrever nela.
 * Agora é texto do quadro: gravada junto da tabela, na mesma versão e no mesmo botão.
 *
 * <p><b>O que se perdeu ao virar editável:</b> o negrito, o `código` e os links que o JSX
 * desenhava. É texto puro, porque a regra da casa é que o texto do time sai como foi
 * digitado — renderizar markdown mudaria o que está escrito, e um `*` de lista viraria
 * itálico. Linha em branco continua separando parágrafo, que é espaçamento e não
 * marcação.
 *
 * <p>O {@code alerta} é a caixa vermelha: existe onde a nota descreve algo que <b>já
 * quebrou</b> ou está prestes a quebrar. Qualquer quadro pode ganhar a sua — basta
 * escrever no campo.
 */
const PROSA: Record<string, { texto: string; alerta?: string }> = {
  'tipos-de-cena': {
    texto:
      'A cena é a unidade mínima do jogo. Nada em Mistik Island acontece fora dela, e o ' +
      'jogo inteiro é um grafo de cenas ligadas por escolhas. Cada uma tem identificador ' +
      'próprio e entrega imagem, som, música, narração e texto; abaixo, as ações ' +
      'possíveis naquela situação. Escolher uma leva ao desenvolvimento da própria cena, ' +
      'a outra cena, ou de volta.\n\n' +
      'A geografia é mecânica, não pano de fundo. São três níveis: a região é a unidade ' +
      'cultural e econômica (sete delas, cada uma com seus povos e sua produção), a área ' +
      'é a unidade de deslocamento (sete por região, 49 no total) e o local é a unidade ' +
      'de interação — taverna, mina, castelo —, que pode conter outros locais mais ' +
      'internos. Quarenta e nove áreas, e o jogador conhece bem três: é por isso que o ' +
      'mapa de outro jogador vale ouro.\n\n' +
      'Por cima disso vêm as variantes: a mesma taverna tem versão de primeira visita, ' +
      'versão padrão e versão em chamas, escolhidas por condição e prioridade. Um evento ' +
      'global troca a variante para todos os jogadores ao mesmo tempo, e é o que faz a ' +
      'ilha ter história própria durante a temporada — quem chegou antes do incêndio ' +
      'conheceu o bardo; quem chegou depois nunca vai ouvir aquela canção nesta Season.\n\n' +
      'Sinal de quebra, na palavra do GDD: cena com uma única saída útil não é cena, é ' +
      'corredor. E se as variantes forem puramente cosméticas, o sistema vira custo de ' +
      'produção sem retorno de design.',
  },
  custos: {
    texto:
      'O dia do jogador é limitado por dois recursos com funções diferentes. A energia é ' +
      'o teto real de quanto ele consegue fazer; o turno é o relógio do mundo, que ' +
      'decide o que está disponível. Se existisse só energia, o jogador faria tudo em ' +
      'qualquer ordem e a ilha não teria horário; se existisse só turno, bastaria contar ' +
      'até doze. É o cruzamento dos dois que produz o planejamento diário — e é a ' +
      'mudança mais estrutural do GDD v3.3.\n\n' +
      'Energia negativa aplica a condição esgotado: nenhuma ação com custo de energia ' +
      'fica disponível, e a única saída é dormir. Daí sai o loop econômico central do ' +
      'jogo: ouro paga dormida melhor, dormida melhor devolve mais energia para amanhã, ' +
      'mais energia rende mais ouro. Descansar bem é investimento, não conforto.\n\n' +
      'A coluna da fórmula é a de "O teste base" instanciada por ação: o atributo ' +
      'primário conta ×2, o secundário ×1. Quem manda em qual ação é o que liga build a ' +
      'caminho de progressão — o coletor vive de Percepção, o mineiro de Vitalidade, o ' +
      'alquimista de Intelecto —, e é por isso que a tabela vale mais que os ' +
      'multiplicadores exatos, que pertencem ao balanceamento.',
    alerta:
      'A energia não existe no motor. Nenhum dos 20 efeitos do catálogo a toca, e o ' +
      'único custo que uma ação sabe cobrar é costTurns. Toda a coluna de energia desta ' +
      'tabela é, hoje, uma pergunta sem implementação — e é o conflito de maior peso ' +
      'entre o épico-01 e o GDD.',
  },
  'ordem-da-acao': {
    texto:
      'Toda ação executa na mesma sequência, e a ordem não é detalhe de implementação: ' +
      'ela decide o que acontece quando duas regras se cruzam. O custo sai primeiro, ' +
      'depois os efeitos, depois a navegação, e por último a resposta narrativa.\n\n' +
      'A visibilidade das ações é recalculada a cada render, então um efeito que seta ' +
      'flag ou counter pode revelar outra ação na mesma cena — acender a tocha faz ' +
      'aparecer "atravessar a caverna". É o mecanismo que faz uma cena reagir sem ' +
      'recarregar nada.\n\n' +
      'Duas precedências que já pegaram gente de surpresa: goto_scene (efeito) vence o ' +
      'targetScene da ação, e start_minigame suprime a navegação por defaultTarget. E o ' +
      'skill check não usa o effects base — ele bifurca em successEffects e failEffects, ' +
      'que é onde o conteúdo de um ramo costuma sumir quando foi escrito no lugar errado.',
  },
  'teste-base': {
    texto:
      'Um único jeito de perguntar ao mundo se deu certo. O propósito, no GDD, é ' +
      'legibilidade: o jogador precisa conseguir olhar uma ação e estimar a chance antes ' +
      'de gastar turno e energia — sem isso, decidir vira apostar. Passar pela sala do ' +
      'troll adormecido tem número declarado, e ele escolhe assim mesmo.\n\n' +
      'A conta soma o dado de doze faces, o atributo primário com peso dobrado, os ' +
      'secundários, o nível da perícia, o equipamento ou ferramenta e os bônus de ' +
      'situação, menos as penalidades ativas. O resultado é comparado com a dificuldade ' +
      'declarada da ação, com a da receita, ou com o teste do oponente.\n\n' +
      'São três alavancas para o mesmo resultado, e é a diferença entre elas que sustenta ' +
      'a progressão: atributo é caro e lento, perícia sobe com uso, ferramenta se compra ' +
      'e quebra. Sinal de quebra, na palavra do GDD: se o jogador não souber explicar por ' +
      'que falhou, a mecânica quebrou; se o dado pesar mais que tudo o que ele construiu, ' +
      'a progressão não é sentida.\n\n' +
      'Diretriz de conteúdo do épico-01: um teste com dado só entrega emoção na zona de ' +
      'incerteza, entre 30% e 70% de sucesso. O que sempre passa ou nunca passa deveria ' +
      'ser ação comum, não teste — CD nula ou 0 dá sucesso garantido e 999 dá falha ' +
      'garantida, e as duas servem para forçar um ramo em teste, não para conteúdo real.',
    alerta:
      'Existem dois críticos convivendo com o mesmo nome: o crítico do dado, que altera ' +
      'o resultado do teste, e o crítico de ataque, que multiplica dano. O GDD registra ' +
      'como inconsistência aberta — precisam ser renomeados ou unificados. E a fuga e a ' +
      'perseguição usam um dado diferente de todo o resto do jogo, o que quebra a régua ' +
      'mental do jogador sem ganho aparente.',
  },
  tokens: {
    texto:
      'A linguagem que decide se uma cena pode ser entrada, se uma conexão pode ser ' +
      'percorrida e se uma ação aparece. É o que permite, na frase do GDD, que uma porta ' +
      'exista na cena e não esteja liberada — e que o conteúdo de uma cena seja função do ' +
      'estado do mundo, de modo que adiar tenha um custo que não aparece no relógio.\n\n' +
      'O checker é reavaliado a cada render: ganhar um ponto de atributo ou um item muda ' +
      'a tela na hora. É também o ponto de integração das world-quests, que trouxeram ' +
      'cinco tokens próprios (world_flag, world_counter, world_top, contributed e ' +
      'world_quest_state) — qualquer endurecimento do checker precisa preservá-los.\n\n' +
      'Ação travada pode aparecer travada ou sumir, conforme hiddenWhenBlocked. A ' +
      'diferença é de design, não de implementação: travada visível cria aspiração, ' +
      'oculta guarda segredo, e escolher errado ou frustra ou entrega o final antes da ' +
      'hora.',
    alerta:
      'Duas falhas conhecidas. Um token desconhecido LIBERA a ação: um typo de admin ' +
      '(flgg:x, attr:strenght) abre conteúdo de segredo para todos, e o fail-open tem ' +
      'três fontes distintas no código. E o time:night compara o turno ABSOLUTO, que ' +
      'nunca reseta sozinho — a partir do segundo dia ele tende a ficar sempre ' +
      'verdadeiro. Os cenários atuais passam porque o personagem está no dia 1.',
  },
  reputacao: {
    texto:
      'Toda relação social do jogador é um número rastreado em duas dimensões: por NPC e ' +
      'por facção. O efeito delta_rep sobe ou desce esse valor numa ação, e o gate rep:* ' +
      'abre ou fecha conteúdo conforme o acumulado. Não há auto-propagação entre as duas ' +
      'dimensões — cada alvo é explícito, e é possível ser querido por um khotla e ' +
      'inimigo dos khotla.\n\n' +
      'É moeda narrativa de verdade: o Decano só concede audiência a quem reuniu ' +
      'endossos de três NPCs diferentes, e um único insulto pode fechar uma sala para o ' +
      'resto da temporada.\n\n' +
      'A curva é assimétrica, e vale decidir se é de propósito: há duas faixas negativas ' +
      'contra quatro positivas. Se a narrativa de inimizade precisar de mais degraus, ' +
      'falta um "Inimigo Jurado" na ponta de baixo.',
    alerta:
      'Os limiares abaixo só existem hoje dentro de um step do Cypress. Antes de o HUD ' +
      'de reputação existir, eles precisam morar no backend — senão nascem duas réguas ' +
      'para a mesma medida. E o GDD v3.3 não menciona reputação em seção nenhuma, nem ' +
      'como sistema canônico, nem como sistema em aberto: o motor está pronto para uma ' +
      'mecânica que o cânone de design ainda não reconheceu.',
  },
  'faixas-do-dia': {
    texto:
      'A ilha tem horário, e é o que separa "o que eu faço" de "quando eu faço". NPCs ' +
      'abrem e fecham em faixas próprias, recursos aparecem em janelas próprias, e ' +
      'algumas criaturas só existem à noite ou de madrugada. O ferreiro fecha antes do ' +
      'pôr do sol; a flor de lótus negra só abre na madrugada.\n\n' +
      'Nada disso é comunicado em tela de ajuda: o jogador descobre observando, errando e ' +
      'conversando. É a mecânica que mais recompensa observação — e a que mais depende de ' +
      'o mundo ser descobrível de dentro. Se a rotina não puder ser aprendida jogando, o ' +
      'jogador vai consultar planilha de terceiros e a mecânica migra para fora do ' +
      'produto.\n\n' +
      'Por cima do dia do personagem corre o dia global do mundo, que avança uma vez por ' +
      'dia real e faz a ilha viver mesmo para quem não está jogando. O limite duro é um ' +
      'dia de personagem por dia real.',
    alerta:
      'O motor roda com OITO turnos por dia; o design define doze. A migration V18 semeia ' +
      'turn.per_day = 8, e toda a matemática de períodos e de dormir só fecha com 12. ' +
      'Além disso, o HUD cai num fallback de apenas duas fases quando time.phases não ' +
      'está cadastrado, e o reset do mundo roda às 6 UTC — que é 03:00 em Brasília, três ' +
      'horas antes do que o design pede.',
  },
  efeitos: {
    texto:
      'O catálogo declarativo que uma ação compõe sem código: flags, counters, atributo, ' +
      'ouro, item, navegação, reputação, companheiro e as escritas coletivas de ' +
      'world-quest. É o que faz escolhas deixarem marca sem passar por deploy — acender a ' +
      'tocha seta a flag que revela a passagem, treinar três vezes libera o duelo.\n\n' +
      'É também onde o conteúdo cruza com outros épicos: o gatilho que incrementa um ' +
      'counter ao vencer um combate depende do Épico 7, e o sorteio de encontro selvagem ' +
      'ao gastar tempo no mundo depende dos Épicos 6 e 8. Nenhum dos dois existe hoje — ' +
      'as duas últimas linhas da tabela estão aqui porque o GDD as exige, não porque o ' +
      'motor as tenha.',
    alerta:
      'Os efeitos são best-effort dentro de uma transação: um efeito que lança não aborta ' +
      'os demais, e o que já salvou continua salvo — mas o execute é @Transactional, e ' +
      'uma exceção que escape dele reverte o turno. Há tensão real entre as duas camadas. ' +
      'Somam-se a isso dois comportamentos que mordem: dec_counter não tem piso (de 0 vai ' +
      'a −1) e delta_gold satura em 0, então comprar sem saldo zera o ouro e entrega o ' +
      'item de graça.',
  },
  'tipos-de-acao': {
    texto:
      'Toda interação dentro de uma cena é uma ação: um botão com rótulo que executa e ' +
      'devolve uma resposta narrativa, inline (substituindo o texto da cena) ou em ' +
      'pergaminho. O modelo é o que decide se o desfecho é certo ou jogado, e é a única ' +
      'escolha estrutural que quem escreve conteúdo precisa fazer antes de tudo.\n\n' +
      'São três no código. O épico-01 documenta dois — o terceiro existe no enum e não ' +
      'aparece em nenhuma feature, o que costuma significar uma de duas coisas: ou ele ' +
      'nasceu para um caso que morreu, ou está esperando um épico que ninguém escreveu.',
  },
  campos: {
    texto:
      'Os campos que mudam o comportamento sem mudar o conteúdo. São eles que separam ' +
      'uma cena que se comporta como corredor de uma que se comporta como encruzilhada, ' +
      'e o designer os cadastra pelo admin, sem deploy.\n\n' +
      'Dois merecem atenção por serem os que criam soft-lock quando combinados sem ' +
      'cuidado: block_back esconde a saída de volta, e uma condição não satisfazível ' +
      'fecha as demais. Cena sem saída alcançável é o risco de maior severidade do ' +
      'épico-01, e a mitigação prevista é um validador de alcançabilidade no admin mais ' +
      'telemetria de "0% de saída".',
  },
  comparadores: {
    texto:
      'Como os tokens de Condições se comparam e se combinam. Vale igual para cena, ' +
      'conexão e ação — é a mesma gramática nos três lugares, o que significa que quem ' +
      'aprende a escrever um gate sabe escrever todos.\n\n' +
      'A composição booleana é o que permite expressar regra real sem código: ' +
      'attr:strength:GT:5,!flag:cursed é "força maior que 5 e não amaldiçoado"; ' +
      '(attr:strength:GTE:7|attr:agility:GTE:7),!flag:pacifista abre a mesma porta para ' +
      'dois tipos de personagem e a fecha para um terceiro.',
  },
  decisoes: {
    texto:
      'As dez perguntas que o épico-01 levanta e não responde. Cada uma traz a ' +
      'recomendação do próprio épico; a palavra final é do time, e a última coluna está ' +
      'aqui para recebê-la.\n\n' +
      'Quatro bloqueiam a carga de conteúdo real e deveriam fechar antes de qualquer ' +
      'escrita em massa: o conjunto de atributos, o piso do dec_counter, a compra sem ' +
      'saldo e as faixas de reputação. Uma quinta, a fórmula do teste, é decisão-fundação ' +
      '— o resolver é compartilhado com o Combate, e mexer nele depois de calibrar CDs e ' +
      'dano significa rebalancear os dois.',
  },
  conflitos: {
    texto:
      'Só entra aqui o que é contradição: pontos em que seguir uma fonte implica não ' +
      'seguir a outra, e em que a implementação teria de escolher no escuro. Diferença de ' +
      'vocabulário, e assunto que um trata e o outro cala, ficaram de fora.\n\n' +
      'A regra de precedência que o próprio GDD adota é útil para decidir: o ' +
      'Posicionamento manda no porquê, as Mecânicas Místikas mandam no como, e o GDD ' +
      'antigo só vale onde os outros calam. Onde a divergência é sobre um catálogo — ' +
      'quantos tipos de cena existem, quais efeitos há —, quem desempata é o código, ' +
      'porque ele é o que roda.\n\n' +
      'A coluna de decisão nasce vazia de propósito: um chute pré-preenchido se lê como ' +
      'decisão tomada, e o time passaria a discutir o que a tela inventou.',
  },
};

/**
 * As tabelas na ordem em que a página as desenha.
 *
 * <p>A ordem é de leitura, e foi decidida pelo Theo: primeiro o que o jogador
 * <b>encontra</b> (a cena), depois o que ele <b>faz</b> (a ação, o que ela cobra, como ela
 * é resolvida), depois o que o mundo <b>responde</b> (efeitos, reputação, relógio), e só
 * então o vocabulário do admin e as perguntas em aberto.
 *
 * <p><b>As chaves não acompanham os renomes.</b> `custos` continua sendo `custos` embora a
 * seção se chame "As Ações", e `tipos-de-acao` continua com esse nome embora a seção se
 * chame "Modelos de ação no sistema". A chave é onde o conteúdo do time está gravado —
 * renomeá-la abandonaria o que já foi escrito naquele quadro, e nenhum rótulo vale isso.
 */
const QUADROS: Record<string, Tabela> = {
  'tipos-de-cena': TIPOS_DE_CENA,
  custos: ACOES,
  'ordem-da-acao': ORDEM_DA_ACAO,
  'teste-base': TESTE_BASE,
  tokens: TOKENS,
  reputacao: REPUTACAO,
  'faixas-do-dia': FAIXAS_DO_DIA,
  efeitos: EFEITOS,
  'tipos-de-acao': TIPOS_DE_ACAO,
  campos: CAMPOS,
  comparadores: COMPARADORES,
  decisoes: DECISOES,
  conflitos: CONFLITOS,
};

/**
 * Qual família pinta cada linha, nos dois quadros que agrupam por parentesco.
 *
 * <p>Fora das tabelas, e não dentro delas, para a lista de famílias caber num olhar: aqui
 * dá para conferir a taxonomia inteira sem rolar por trinta linhas de conteúdo, e é assim
 * que se percebe que "As Cenas" e "As Ações" usam a mesma.
 *
 * <p>A ordem acompanha a das linhas de cada tabela. Isso é frágil de propósito — o
 * comentário ao lado de cada família diz a que linha ela pertence, e uma linha nova no
 * meio pede que este bloco seja relido. A alternativa, casar por texto da célula, quebra
 * silenciosamente no dia em que alguém renomeia "Caçar" para "Caçada".
 */
const FAMILIAS_DAS_LINHAS: Record<string, Record<string, string>> = {
  /*
   * START é `evento` porque é o começo — a mesma cor do que acontece. MOB também: um
   * encontro É um evento, e o próprio GDD lista "Encontros" e "Encontro Selvagem" dentro
   * de Eventos. MERC acompanha NPC, porque a loja é o NPC atendendo.
   *
   * JORNAL e RANK são as duas atribuições fracas da tabela: são telas do sistema, e
   * nenhuma das cinco famílias fala delas. Ficaram no que mais se aproxima — o jornal
   * lista quests, o ranking é sobre outras pessoas —, e a coluna de cor existe para o
   * time discordar sem precisar de commit.
   */
  'tipos-de-cena': familiasNaOrdem(
    FAMILIAS.evento, // START — o começo
    FAMILIAS.lugar, // REGIAO
    FAMILIAS.lugar, // AREA
    FAMILIAS.lugar, // PRACA
    FAMILIAS.lugar, // LOCAL
    FAMILIAS.npc, // NPC
    FAMILIAS.evento, // MOB — encontro de combate
    FAMILIAS.producao, // REC
    FAMILIAS.evento, // EVENTO
    FAMILIAS.npc, // MERC
    FAMILIAS.evento, // JORNAL — lista quests
    FAMILIAS.npc, // RANK — é sobre outras pessoas
    FAMILIAS.producao // Criação (só no GDD)
  ),

  /*
   * Coleta e criação partilham o amarelo: o que se tira do mundo e o que se faz com isso
   * são o mesmo caminho, e separá-los em dois tons dizia uma distinção que a tabela já
   * faz com palavras. Escrever e estudar entram junto — o que sai deles é produção, só
   * que de conhecimento. Combater é `evento`, como o MOB da outra tabela.
   */
  custos: familiasNaOrdem(
    FAMILIAS.lugar, // Viajar
    FAMILIAS.lugar, // Deslocar
    FAMILIAS.lugar, // Explorar
    FAMILIAS.producao, // Caçar
    FAMILIAS.producao, // Coletar
    FAMILIAS.producao, // Minerar
    FAMILIAS.producao, // Alquimia
    FAMILIAS.producao, // Forjar
    FAMILIAS.producao, // Couraria
    FAMILIAS.producao, // Escrever
    FAMILIAS.producao, // Estudar
    FAMILIAS.descanso, // Descansar
    FAMILIAS.evento, // Combater
    FAMILIAS.npc, // Conversar / negociar / usar item / interagir
    FAMILIAS.evento, // Ação de cena GENERIC — é o que faz acontecer
    FAMILIAS.evento // Ação de cena SKILL_CHECK — idem, com dado
  ),
};

/** Cada quadro com a sua prosa e, onde há, as cores das linhas. */
export const TABELAS: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS).map(([chave, quadro]) => [
    chave,
    { ...PROSA[chave], familias: FAMILIAS_DAS_LINHAS[chave], ...quadro },
  ])
);

/**
 * A forma de uma seção é a do {@link PaginaDeQuadros} — título, rótulo do item, numerada,
 * destaque, cores, ícones. Era uma interface própria daqui, de quando só esta página
 * tinha quadros; com três páginas no mesmo componente, uma cópia local divergiria no
 * primeiro campo novo.
 */
export type Secao = SecaoDeQuadros;

/**
 * O título de cada seção, e como ela se desenha.
 *
 * <p><b>O título saiu do JSX por causa do log de alterações.</b> Ele registra qual quadro
 * mudou, e o log precisa dizer "As Cenas" onde o banco guarda `tipos-de-cena` — com o
 * título escrito na página, o log teria de manter a própria tradução, e as duas
 * divergiriam no primeiro renome. É a mesma razão pela qual o nome da tela mora em
 * `menu.ts` e não no cabeçalho dela.
 *
 * <p>A ordem que vale é a de {@link TABELAS}; este mapa só responde "como se chama" e
 * "como se desenha".
 */
const SECOES_BASE: Record<string, Secao> = {
  'tipos-de-cena': { titulo: 'As Cenas', rotuloDoItem: 'tipo' },
  custos: { titulo: 'As Ações', rotuloDoItem: 'ação' },
  'ordem-da-acao': {
    titulo: 'A ordem que uma ação acontece',
    rotuloDoItem: 'etapa',
    numerada: true,
  },
  'teste-base': { titulo: 'O teste base', rotuloDoItem: 'parcela' },
  tokens: { titulo: 'Condições', rotuloDoItem: 'token' },
  reputacao: { titulo: 'Reputação', rotuloDoItem: 'faixa' },
  'faixas-do-dia': { titulo: 'As faixas do dia', rotuloDoItem: 'faixa' },
  efeitos: { titulo: 'Efeitos', rotuloDoItem: 'efeito' },
  'tipos-de-acao': { titulo: 'Modelos de ação no sistema', rotuloDoItem: 'kind' },
  campos: { titulo: 'Campos de cena, conexão e ação', rotuloDoItem: 'campo' },
  comparadores: { titulo: 'Comparadores e composição', rotuloDoItem: 'comparador' },
  decisoes: { titulo: 'Decisões que o épico já pedia', rotuloDoItem: 'decisão' },
  conflitos: { titulo: 'Conflitos a decidir', rotuloDoItem: 'conflito', destaque: true },
};

/**
 * O ícone de cada seção, e — onde o texto da linha identifica uma coisa do jogo — o de
 * cada linha. As Cenas resolvem pelo código do tipo ("AREA"), as Ações pela primeira
 * palavra ("Viajar (troca de área)"). Ver {@code iconesDeConteudo}.
 */
const ICONES: Record<string, Pick<Secao, 'icone' | 'iconeDaLinha'>> = {
  'tipos-de-cena': {
    icone: iconeDoMenu('sol'),
    iconeDaLinha: (l) => iconeDeTipoDeCena(l.valores.tipo ?? ''),
  },
  custos: { icone: iconeDoMenu('fluxo'), iconeDaLinha: (l) => iconeDeAcao(l.valores.acao ?? '') },
  'ordem-da-acao': { icone: iconeDoMenu('transcricao') },
  'teste-base': { icone: iconeDoMenu('dado') },
  tokens: { icone: iconeDoMenu('chave') },
  reputacao: { icone: iconeDoMenu('estandarte') },
  'faixas-do-dia': { icone: iconeDoMenu('calendario') },
  efeitos: { icone: iconeDoMenu('pocao') },
  'tipos-de-acao': { icone: iconeDoMenu('quadro') },
  campos: { icone: iconeDoMenu('documento') },
  comparadores: { icone: iconeDoMenu('barras') },
  decisoes: { icone: iconeDoMenu('rosacea') },
  conflitos: { icone: iconeDoMenu('alvo') },
};

export const SECOES: Record<string, Secao> = Object.fromEntries(
  Object.entries(SECOES_BASE).map(([chave, s]) => [chave, { ...s, ...ICONES[chave] }])
);

/**
 * Os macro-títulos e o índice — o padrão das três páginas de quadros (Theo, 19/08/2026).
 *
 * <p>A ordem dos quadros é a que a página sempre teve; os grupos só dão nome ao que já
 * estava junto. Mexer na ordem seria mexer numa tela que o time abre todo dia.
 */
export const GRUPOS: GrupoDeQuadros[] = [
  { id: 'motor', titulo: 'O motor', quadros: ['tipos-de-cena', 'custos', 'ordem-da-acao', 'teste-base'] },
  {
    id: 'requisitos',
    titulo: 'Requisitos, reputação e efeitos',
    quadros: ['tokens', 'reputacao', 'faixas-do-dia', 'efeitos'],
  },
  { id: 'sistema', titulo: 'O sistema por dentro', quadros: ['tipos-de-acao', 'campos', 'comparadores'] },
  { id: 'disputa', titulo: 'Em disputa', quadros: ['decisoes', 'conflitos'] },
];

/*
 * Não há mais `tituloDaSecao` aqui: a tradução quadro → título (com a queda na própria
 * chave, para log de seção renomeada continuar legível) foi para o `PaginaDeQuadros`,
 * que a deriva de `SECOES` — é ele quem desenha o log, nas duas páginas.
 */

